import { useCallback } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid";

import TableCellTruncated from "../../../../../../common/components/table/TableCellTruncated";
import useLocationFromCache from "../../../../../../common/hooks/useLocationFromCache";
import { formatTimestamp } from "../../../../../../common/utils/timestampUtils";
import FeedbackThumbsTableContainer from "../FeedbackThumbsTableContainer";
import RecentEventsHoverIcons from "../RecentEventsHoverIcons";

const MITIGATED_RISK_VISIBLE_COUNT = 2;

export const useGetRecentEventsTableColumns = () => {
  const { getCachedLocationName } = useLocationFromCache();

  const columns: GridColDef[] = [
    {
      field: "rowIndex",
      headerName: "#",
      width: 50,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      type: "number",
      renderCell: (params): JSX.Element | null => {
        return <Typography variant="body2">{params.row.rowIndex}</Typography>;
      },
    },
    {
      field: "keyFrame",
      headerName: "Snapshot",
      flex: 1,
      minWidth: 320,
      width: 320,
      display: "flex",
      align: "center",
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      cellClassName: "keyFrameCell",
      renderCell: (params): JSX.Element | null => {
        const keyFrame = params?.row?.signedUrlKeyFrame ?? "";

        const mediaOutput = params?.row?.signedUrlMediaOutput ?? "";

        const memoizedKeyFrameClickCallback = useCallback((): void => {
          params?.row?.onKeyFrameClick && params?.row?.onKeyFrameClick(params);
        }, [params?.row]);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.75em",
              cursor: "pointer",
              position: "relative",
            }}
          >
            <RecentEventsHoverIcons
              keyFrame={keyFrame}
              mediaOutput={mediaOutput}
              onClick={memoizedKeyFrameClickCallback}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.25em",
              }}
            >
              <Typography variant="buttonMedium" className="model">
                {params?.row?.model}
              </Typography>
              <Typography variant="caption">
                {formatTimestamp(params?.row.timestamp)}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "feedback",
      headerName: "feedback",
      width: 100,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        return <FeedbackThumbsTableContainer params={params} />;
      },
    },
    {
      field: "mitigatedRisk",
      headerName: "Mitigated Risk",
      width: 220,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        const mitigatedRisk = params?.row?.mitigatedRisk ?? [];

        let chunkedMitigatedRisk = mitigatedRisk ?? [];

        let otherRiskCount = 0;

        if (mitigatedRisk.length > MITIGATED_RISK_VISIBLE_COUNT) {
          chunkedMitigatedRisk = mitigatedRisk.slice(
            0,
            MITIGATED_RISK_VISIBLE_COUNT
          );

          otherRiskCount = mitigatedRisk.length - MITIGATED_RISK_VISIBLE_COUNT;
        }

        return (
          <Box
            sx={{
              display: "flex",
              gap: "0.5em",
              alignItems: "center",
            }}
          >
            <Box
              sx={{ display: "flex", gap: "0.5em", flexDirection: "column" }}
            >
              {chunkedMitigatedRisk.map((risk: string) => (
                <Typography key={risk} component="div" variant="body2Regular">
                  {risk}
                </Typography>
              ))}
            </Box>

            {mitigatedRisk.length > MITIGATED_RISK_VISIBLE_COUNT && (
              <Tooltip
                title={
                  <ul
                    style={{
                      margin: 0,
                      padding: "2em",
                    }}
                  >
                    {mitigatedRisk.map((risk: string) => (
                      <li key={risk}>
                        <Typography variant="body2Regular">{risk}</Typography>
                      </li>
                    ))}
                  </ul>
                }
                placement="top-start"
                arrow
              >
                <Button
                  sx={{
                    display: "block",
                    padding: 0,
                    minWidth: "26px",
                    width: "26px",
                    height: "26px",
                    borderRadius: "8px",
                    color: theme => theme.palette.otherTextTertiary.main,
                    border: theme =>
                      `1px solid ${theme.palette.otherOutlineBorder.main}`,

                    "&:hover": {
                      // color: theme => theme.palette.otherTextTertiary.main,
                      border: theme =>
                        `1px solid ${theme.palette.otherTextTertiary.main}`,
                    },
                  }}
                  variant="outlined"
                >
                  <Typography
                    component="div"
                    variant="caption"
                    color={theme => theme.palette.otherTextTertiary.main}
                  >
                    +{otherRiskCount}
                  </Typography>
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (locationId?: string | null): string => {
        const locationName = getCachedLocationName(locationId);

        return locationName;
      },
      renderCell: (params): JSX.Element | null => {
        const locationName = getCachedLocationName(params?.row?.location);

        return (
          <TableCellTruncated
            clickableCursor
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{
              variant: "body2",
            }}
          />
        );
      },
    },
    {
      field: "cameraId",
      headerName: "DEVICE ID",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null =>
        params ? (
          <TableCellTruncated
            clickableCursor
            tooltipText={params?.row.cameraId}
            text={params?.row.cameraId}
            TypographyProps={{
              variant: "body2Bold",
            }}
          />
        ) : null,
    },
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 1,
      minWidth: 180,
      hideable: false,
      disableColumnMenu: true,
      sortable: true,
      type: "dateTime",
      valueGetter: (timestamp: string): Date => {
        return new Date(formatTimestamp(timestamp));
      },
      renderCell: (params): JSX.Element | null => {
        const date = formatTimestamp(params?.row.timestamp);

        return (
          <TableCellTruncated clickableCursor tooltipText={date} text={date} />
        );
      },
    },
    {
      field: "zoneName",
      headerName: "Zone",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: true,
      valueGetter: (zoneName?: string | null): string => {
        return zoneName ?? "";
      },
      renderCell: (params): JSX.Element | null => {
        const zoneName = params.row.zoneName ?? "";

        return (
          <TableCellTruncated
            clickableCursor
            tooltipText={zoneName}
            text={zoneName}
          />
        );
      },
    },
  ];

  return { columns };
};
